import {
  Box,
  RangeSlider as MantineRangeSlider,
  RangeSliderProps as MantineRangeSliderProps,
  Slider as MantineSlider,
  SliderProps as MantineSliderProps,
  extractStyleProps,
} from '@mantine/core';
import { clamp, useUncontrolled } from '@mantine/hooks';
import clsx from 'clsx';
import { ReactNode, forwardRef, useState } from 'react';

import { NumberInput, NumberInputProps } from '../NumberInput';
import styles from './Slider.module.css';

type OmittedMantineProps =
  | 'variant'
  | 'color'
  | 'styles'
  | 'classNames'
  | 'style'
  | 'size'
  | 'vars'
  | 'thumbSize'
  | 'radius'
  | 'orientation';

type SliderMark = NonNullable<MantineSliderProps['marks']>[number];

export type SliderInputProps = Omit<
  NumberInputProps,
  'value' | 'onChange' | 'min' | 'max'
>;

export interface SliderProps extends Omit<
  MantineSliderProps,
  OmittedMantineProps
> {
  /** Text under the start of the track. With `withInput`, rendered as the prefix left of the track instead */
  minLabel?: ReactNode;
  /** Text under the end of the track. Not rendered with `withInput` */
  maxLabel?: ReactNode;
  /** Render a NumberInput right of the track, synced with the slider value */
  withInput?: boolean;
  /** Props forwarded to the NumberInput rendered by `withInput` */
  inputProps?: SliderInputProps;
}

export interface RangeSliderProps extends Omit<
  MantineRangeSliderProps,
  OmittedMantineProps
> {
  /** Text under the start of the track */
  minLabel?: ReactNode;
  /** Text under the end of the track */
  maxLabel?: ReactNode;
}

const sliderClassNames = {
  root: styles.root,
  trackContainer: styles.trackContainer,
  track: styles.track,
  bar: styles.bar,
  thumb: styles.thumb,
  label: styles.label,
  markWrapper: styles.markWrapper,
  mark: styles.mark,
  markLabel: styles.markLabel,
};

// Mantine always emits `--slider-size: var(--slider-size-md)` inline, which is undefined under `unstyled`.
const sliderVars = () => ({
  root: {
    '--slider-size': 'var(--space-6)',
    '--slider-thumb-size': 'var(--space-20)',
  },
});

const isRendered = (node: ReactNode) => node !== null && node !== undefined;

const hasVisibleMarkLabels = (marks?: SliderMark[]) =>
  !!marks?.some((mark) => !mark.hidden && isRendered(mark.label));

const getStepDecimals = (step: number) =>
  (step.toString().split('.')[1] ?? '').length;

interface ScaleLabelsProps {
  minLabel?: ReactNode;
  maxLabel?: ReactNode;
}

const ScaleLabels = ({ minLabel, maxLabel }: ScaleLabelsProps) => {
  if (!isRendered(minLabel) && !isRendered(maxLabel)) return null;

  return (
    <div className={styles.scale}>
      <span>{minLabel}</span>
      <span>{maxLabel}</span>
    </div>
  );
};

export const Slider = forwardRef<HTMLDivElement, SliderProps>(
  (
    {
      minLabel,
      maxLabel,
      withInput = false,
      inputProps,
      className,
      value,
      defaultValue,
      onChange,
      onChangeEnd,
      min = 0,
      max = 100,
      step = 1,
      precision,
      disabled,
      marks,
      ...others
    },
    ref
  ) => {
    const { styleProps, rest } = extractStyleProps(others);
    const [currentValue, setValue] = useUncontrolled({
      value,
      defaultValue,
      finalValue: clamp(0, min, max),
      onChange,
    });
    const [inputDraft, setInputDraft] = useState<number | string | null>(null);

    const slider = (
      <MantineSlider
        ref={ref}
        unstyled
        classNames={sliderClassNames}
        vars={sliderVars}
        data-mark-labels={hasVisibleMarkLabels(marks) || undefined}
        value={currentValue}
        onChange={setValue}
        onChangeEnd={onChangeEnd}
        min={min}
        max={max}
        step={step}
        precision={precision}
        disabled={disabled}
        marks={marks}
        {...rest}
      />
    );

    if (!withInput) {
      return (
        <Box
          {...styleProps}
          className={clsx(styles.wrapper, className)}
          mod={{ disabled }}
        >
          {slider}
          <ScaleLabels minLabel={minLabel} maxLabel={maxLabel} />
        </Box>
      );
    }

    return (
      <Box
        {...styleProps}
        className={clsx(styles.wrapper, className)}
        mod={{ disabled, 'with-input': true }}
      >
        {isRendered(minLabel) && (
          <span className={styles.prefix}>{minLabel}</span>
        )}
        <div className={styles.sliderArea}>{slider}</div>
        <NumberInput
          size="md"
          hideControls
          step={step}
          decimalScale={precision ?? getStepDecimals(step)}
          disabled={disabled}
          {...inputProps}
          className={clsx(styles.valueInput, inputProps?.className)}
          min={min}
          max={max}
          // The draft keeps partial input (e.g. "5" on the way to "500" with min 100) from snapping while typing.
          value={inputDraft ?? currentValue}
          onChange={(inputValue) => {
            setInputDraft(inputValue);
            if (typeof inputValue === 'number') {
              setValue(clamp(inputValue, min, max));
            }
          }}
          onBlur={(event) => {
            setInputDraft(null);
            onChangeEnd?.(currentValue);
            inputProps?.onBlur?.(event);
          }}
        />
      </Box>
    );
  }
);

Slider.displayName = 'Slider';

export const RangeSlider = forwardRef<HTMLDivElement, RangeSliderProps>(
  (
    {
      minLabel,
      maxLabel,
      className,
      step,
      minRange,
      disabled,
      marks,
      labelAlwaysOn,
      ...others
    },
    ref
  ) => {
    const { styleProps, rest } = extractStyleProps(others);

    return (
      <Box
        {...styleProps}
        className={clsx(styles.wrapper, className)}
        mod={{ disabled }}
      >
        <MantineRangeSlider
          ref={ref}
          unstyled
          classNames={sliderClassNames}
          vars={sliderVars}
          data-mark-labels={hasVisibleMarkLabels(marks) || undefined}
          step={step}
          // Mantine's default of 10 blocks small domains such as 0-5.
          minRange={minRange ?? step ?? 1}
          // Mantine's hover state spans the whole root, so it would show both labels at once; CSS reveals only the active thumb's.
          labelAlwaysOn
          data-label-per-thumb={!labelAlwaysOn || undefined}
          disabled={disabled}
          marks={marks}
          {...rest}
        />
        <ScaleLabels minLabel={minLabel} maxLabel={maxLabel} />
      </Box>
    );
  }
);

RangeSlider.displayName = 'RangeSlider';
